import React, { useState, useEffect } from 'react';

/**
 * Hook para detectar e gerenciar safe area insets em dispositivos iOS
 * 
 * @returns {Object} - Objeto com valores de insets (top, bottom, left, right)
 */
export const useSafeAreaInsets = () => {
  const [insets, setInsets] = useState({
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    orientation: 'portrait'
  });
  
  // Detectar se é iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  
  // Função para obter a orientação atual
  const getOrientation = () => {
    return window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';
  };
  
  useEffect(() => {
    if (!isIOS) return; // Só aplicar em dispositivos iOS
    
    // Carregar insets salvos para a orientação atual
    const loadSavedInsets = () => {
      const currentOrientation = getOrientation();
      const savedInsetsKey = `safeAreaInsets_${currentOrientation}`;
      const savedInsets = localStorage.getItem(savedInsetsKey);
      
      if (savedInsets) {
        try {
          const parsed = JSON.parse(savedInsets);
          if (parsed.bottom > 0) { // Verificar se temos um valor válido para bottom
            setInsets({...parsed, orientation: currentOrientation});
            return true;
          }
        } catch (e) {
          console.error('Erro ao carregar safe area insets do cache', e);
        }
      }
      return false;
    };
    
    // Função para detectar e armazenar os valores de safe area
    const detectSafeArea = () => {
      const orientation = getOrientation();
      
      // Criar um elemento temporário para medir os valores reais de safe-area
      const tempEl = document.createElement('div');
      tempEl.style.position = 'fixed';
      tempEl.style.width = '100%';
      tempEl.style.height = '100%';
      tempEl.style.top = '0';
      tempEl.style.left = '0';
      tempEl.style.paddingTop = 'env(safe-area-inset-top)';
      tempEl.style.paddingBottom = 'env(safe-area-inset-bottom)';
      tempEl.style.paddingLeft = 'env(safe-area-inset-left)';
      tempEl.style.paddingRight = 'env(safe-area-inset-right)';
      tempEl.style.zIndex = '-1';
      document.body.appendChild(tempEl);
      
      // Obter os valores computados
      const style = window.getComputedStyle(tempEl);
      const newInsets = {
        top: parseInt(style.paddingTop, 10) || 0,
        bottom: parseInt(style.paddingBottom, 10) || 0, 
        left: parseInt(style.paddingLeft, 10) || 0,
        right: parseInt(style.paddingRight, 10) || 0,
        orientation
      };
      
      // Remover o elemento temporário
      document.body.removeChild(tempEl);
      
      // Caso especial: Se estivermos no portrait e bottom for 0, mas temos um valor salvo de antes...
      if (orientation === 'portrait' && newInsets.bottom === 0) {
        const savedPortraitInsets = localStorage.getItem('safeAreaInsets_portrait');
        if (savedPortraitInsets) {
          try {
            const parsed = JSON.parse(savedPortraitInsets);
            if (parsed.bottom > 0) {
              newInsets.bottom = parsed.bottom; // Usar o valor anterior que sabemos ser correto
            }
          } catch (e) {
            console.error('Erro ao restaurar bottom inset do portrait', e);
          }
        }
      }
      
      // Apenas salvar se algum valor for diferente de zero (significa que temos uma safe area)
      if (newInsets.top > 0 || newInsets.bottom > 0 || newInsets.left > 0 || newInsets.right > 0) {
        // Salvar específico para cada orientação
        localStorage.setItem(`safeAreaInsets_${orientation}`, JSON.stringify(newInsets));
        setInsets(newInsets);
      }
    };
    
    // Inicialização: primeiro tentar carregar valores salvos, depois detectar
    if (!loadSavedInsets()) {
      detectSafeArea();
    }
    
    // Função robusta para lidar com mudanças de orientação
    const handleOrientationChange = () => {
      const currentOrientation = getOrientation();
      
      // Primeiro tentar carregar valores salvos para a nova orientação
      if (!loadSavedInsets()) {
        // Se não temos valores salvos para esta orientação, detectar com delay
        setTimeout(detectSafeArea, 300);
        
        // Fazer uma segunda verificação após mais tempo para garantir
        setTimeout(detectSafeArea, 1000);
      }
      
      // Caso especial para iOS: verificar a transição landscape -> portrait
      if (currentOrientation === 'portrait') {
        // Fazer detecções adicionais para garantir
        setTimeout(() => {
          const orientation = getOrientation();
          if (orientation === 'portrait') {
            detectSafeArea();
          }
        }, 500);
        
        setTimeout(() => {
          const orientation = getOrientation();
          if (orientation === 'portrait') {
            detectSafeArea();
          }
        }, 1500);
      }
    };
    
    // Adicionar listeners para orientação e resize
    window.addEventListener('resize', handleOrientationChange);
    window.addEventListener('orientationchange', handleOrientationChange);
    
    // Verificar periodicamente no início para garantir valores corretos
    const timerIds = [
      setTimeout(detectSafeArea, 500),
      setTimeout(detectSafeArea, 1500),
      setTimeout(detectSafeArea, 3000)
    ];
    
    return () => {
      window.removeEventListener('resize', handleOrientationChange);
      window.removeEventListener('orientationchange', handleOrientationChange);
      timerIds.forEach(id => clearTimeout(id));
    };
  }, [isIOS]);
  
  return insets;
};

export default useSafeAreaInsets; 